import React, { useRef, useState } from 'react';
import { Upload, Pause, Play } from 'lucide-react';
import api from '@/lib/api';
import { BASE, Box } from './ui';

const I = `${BASE}/importador`;
const LIMITE = 250 * 1024 * 1024;
const PARALELO = 3;
const mb = (b) => `${(b / 1048576).toFixed(1)} MB`;
const hex = (buf) => [...new Uint8Array(buf)].map((x) => x.toString(16).padStart(2, '0')).join('');
const chave = (f) => `${f.name}|${f.size}|${f.lastModified}`;
const msg = (e) => e?.response?.data?.detail || e?.message || 'Erro.';

async function enviarArquivo(file, set, pausado, parteBytes = 5 * 1024 * 1024) {
  if (!file.name.toLowerCase().endsWith('.zip')) throw new Error('Somente arquivos .zip.');
  if (file.size > LIMITE) throw new Error('Arquivo acima de 250 MB.');
  const { data: up } = await api.post(`${I}/upload/init`, { nome: file.name, tamanho: file.size, chave: chave(file), parte_bytes: parteBytes });
  const feitas = new Set(up.recebidas.map((p) => p.n));
  const fila = [...Array(up.total_partes).keys()].filter((k) => !feitas.has(k));
  let enviados = feitas.size * up.parte_bytes;
  const t0 = Date.now(); const base = enviados;
  set({ estado: feitas.size ? 'Retomando' : 'Enviando', enviados: Math.min(enviados, file.size), velocidade: 0, retomadas: feitas.size });
  const worker = async () => {
    while (fila.length) {
      if (pausado.current) throw new Error('PAUSADO');
      const k = fila.shift();
      const blob = file.slice(k * up.parte_bytes, Math.min(file.size, (k + 1) * up.parte_bytes));
      const buf = await blob.arrayBuffer();
      const sha = hex(await crypto.subtle.digest('SHA-256', buf));
      for (let t = 0; ; t += 1) {
        try {
          await api.put(`${I}/upload/${up.upload_id}/parte/${k}`, buf, { headers: { 'Content-Type': 'application/octet-stream', 'X-Parte-Sha256': sha }, timeout: 120000 });
          break;
        } catch (e) {
          if (e?.response?.status === 413 && parteBytes > 1024 * 1024) { const err = new Error('PARTE_GRANDE'); throw err; }
          if (t >= 3) throw e; await new Promise((r) => setTimeout(r, 1500 * (t + 1)));
        }
      }
      enviados += blob.size;
      set({ estado: 'Enviando', enviados, velocidade: (enviados - base) / Math.max(1, (Date.now() - t0) / 1000) });
    }
  };
  await Promise.all([...Array(PARALELO)].map(worker));
  set({ estado: 'Conferindo SHA-256', enviados: file.size });
  const { data } = await api.post(`${I}/upload/${up.upload_id}/concluir`, null, { timeout: 600000 });
  return data;
}

export function Envio({ onDone }) {
  const [fila, setFila] = useState([]);
  const pausado = useRef(false);
  const [rodando, setRodando] = useState(false);
  const upd = (i, v) => setFila((f) => f.map((x, j) => (j === i ? { ...x, ...v } : x)));
  const processar = async (lista) => {
    setRodando(true); pausado.current = false;
    for (let i = 0; i < lista.length; i += 1) {
      if (['Recebido', 'Duplicado'].includes(lista[i].estado)) continue;
      try {
        let r;
        try { r = await enviarArquivo(lista[i].file, (v) => upd(i, v), pausado); } catch (e) {
          if (e.message !== 'PARTE_GRANDE') throw e;
          r = await enviarArquivo(lista[i].file, (v) => upd(i, { ...v, info: 'limite do proxy: partes ajustadas para 1 MB' }), pausado, 1024 * 1024);
        }
        upd(i, { estado: r.duplicado ? 'Duplicado' : 'Recebido', info: r.duplicado ? 'mesmo SHA-256 de um pacote já recebido' : `SHA-256 ${r.sha256.slice(0, 12)}… · validação automática iniciada`, enviados: lista[i].file.size });
        onDone();
      } catch (e) {
        upd(i, { estado: e.message === 'PAUSADO' ? 'Pausado' : 'Erro', info: e.message === 'PAUSADO' ? 'retome para continuar de onde parou' : msg(e) });
        if (e.message === 'PAUSADO') break;
      }
    }
    setRodando(false);
  };
  const escolher = (files) => {
    const novos = [...files].map((file) => ({ file, estado: 'Na fila', enviados: 0, velocidade: 0 }));
    const lista = [...fila.filter((x) => !['Recebido', 'Duplicado'].includes(x.estado)), ...novos];
    setFila(lista);
    processar(lista);
  };
  return (
    <Box testid="importador-envio">
      <p className="mb-2 text-sm font-semibold">1. Enviar pacotes do Sponte (.zip, até 250 MB cada)</p>
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white transition-colors hover:bg-slate-700" data-testid="importador-arquivo-label">
          <Upload className="h-4 w-4" /> Escolher arquivo(s)
          <input type="file" accept=".zip,application/zip" multiple className="hidden" disabled={rodando} onChange={(e) => { escolher(e.target.files); e.target.value = ''; }} data-testid="importador-arquivo" />
        </label>
        {rodando && <button type="button" onClick={() => { pausado.current = true; }} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm hover:bg-slate-50" data-testid="importador-pausar"><Pause className="h-4 w-4" />Pausar</button>}
        {!rodando && fila.some((x) => ['Pausado', 'Erro'].includes(x.estado)) && <button type="button" onClick={() => processar(fila)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm hover:bg-slate-50" data-testid="importador-retomar"><Play className="h-4 w-4" />Retomar</button>}
      </div>
      {fila.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="importador-fila">
          {fila.map((x, i) => {
            const pct = Math.round((100 * x.enviados) / x.file.size);
            return (
              <li key={chave(x.file)} className="rounded-lg border p-2 text-xs" data-testid={`importador-fila-item-${i}`}>
                <div className="flex flex-wrap justify-between gap-2"><b className="break-all">{x.file.name}</b>
                  <span data-testid={`importador-fila-estado-${i}`} className={x.estado === 'Erro' ? 'font-bold text-rose-700' : 'font-semibold'}>{x.estado}</span></div>
                <div className="mt-1 h-2 w-full rounded bg-slate-200"><div className="h-2 rounded bg-amber-600 transition-[width] duration-300" style={{ width: `${pct}%` }} /></div>
                <p className="mt-1 text-slate-600">{pct}% · {mb(x.enviados)} de {mb(x.file.size)}{x.velocidade ? ` · ${mb(x.velocidade)}/s` : ''}{x.retomadas ? ` · ${x.retomadas} parte(s) já recebidas reaproveitadas` : ''}{x.info ? ` · ${x.info}` : ''}</p>
              </li>);
          })}
        </ul>)}
      <p className="mt-2 text-xs text-slate-500">Partes de 5 MB com SHA-256 conferido em cada parte e no arquivo final; envio retomável. Guardado no armazenamento privado do Emergent. Nada é importado automaticamente.</p>
    </Box>
  );
}
